// Copyright 2026 TokenLat. Licensed under the Apache License, Version 2.0.
import { useEffect, useRef, useState } from 'react';

import { I18n } from '@coze-arch/i18n';
import { Button, Popconfirm, Toast } from '@coze-arch/bot-semi';

/** Delegation is separate from the PAT itself and never exposes the spend grant. */
export function SpendAuthorization({
  id,
  disabled,
}: {
  id: string;
  disabled: boolean;
}) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitted = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    void fetch('/api/auth/config', {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(response => (response.ok ? response.json() : null))
      .then(config => {
        if (!controller.signal.aborted) {
          setEnabled(config?.model_spend_enabled === true);
        }
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);
  const authorize = async () => {
    if (submitted.current) {
      return;
    }
    submitted.current = true;
    setBusy(true);
    try {
      const response = await fetch('/api/tokenlat/model-spend/pat/authorize', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        redirect: 'error',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const result = await response.json();
      if (result.msg === 'legacy_api_credential_requires_replacement') {
        Toast.error({content:I18n.t('tl_account_pat_replace_legacy'),showClose:false});
        return;
      }
      if (!response.ok || result.authorized !== true) {
        throw new Error('authorization_failed');
      }
      Toast.success({
        content: I18n.t('tl_account_pat_authorized'),
        showClose: false,
      });
    } catch {
      Toast.error({
        content: I18n.t('tl_account_pat_authorization_failed'),
        showClose: false,
      });
    } finally {
      setBusy(false);
      submitted.current = false;
    }
  };
  if (!enabled) {
    return null;
  }
  return (
    <Popconfirm
      title={I18n.t('tl_account_pat_authorize')}
      content={I18n.t('tl_account_pat_consent')}
      onConfirm={authorize}
    >
      <Button theme="borderless" disabled={disabled || busy} loading={busy}>
        {I18n.t('tl_account_pat_authorize')}
      </Button>
    </Popconfirm>
  );
}
